import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { languageNames, supportedLanguages, forceChangeLanguage } from '../i18n/config';

interface LanguageSelectorProps {
  className?: string;
  variant?: 'dropdown' | 'button' | 'mobile';
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className = '', 
  variant = 'dropdown' 
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 현재 언어
  const currentLanguage = i18n.language;
  const currentLanguageName = languageNames[currentLanguage as keyof typeof languageNames] || '🇺🇸 English';
  
  // 국가 코드와 언어 코드 분리 (국기 없이)
  const getCountryAndLanguageCode = (langCode: string) => {
    const countryCodeMap: { [key: string]: string } = {
      'en': 'US',
      'ko': 'KR', 
      'ja': 'JP',
      'zh': 'CN',
      'vi': 'VN',
      'es': 'ES',
      'fr': 'FR',
      'de': 'DE',
      'pt': 'PT',
      'id': 'ID',
      'th': 'TH',
      'tr': 'TR',
      'ru': 'RU',
      'it': 'IT',
      'el': 'GR',
      'hi': 'IN',
      'uk': 'UA',
      'pl': 'PL',
      'nl': 'NL',
      'hu': 'HU',
      'cs': 'CZ',
      'sv': 'SE',
      'fi': 'FI',
      'no': 'NO',
      'ms': 'MY',
      'he': 'IL'
    };
    
    const countryCode = countryCodeMap[langCode] || langCode.toUpperCase();
    const languageCode = langCode.toUpperCase();
    return { countryCode, languageCode };
  };
  
  // 디버깅을 위한 로그 (프로덕션에서는 제거)
  if (process.env.NODE_ENV === 'development') {
    console.log('🌐 LanguageSelector - Current language:', currentLanguage);
    console.log('🌐 LanguageSelector - Current language name:', currentLanguageName);
  }

  // 언어 변경 핸들러 (모바일 앱과 동일한 로직)
  const handleLanguageChange = async (languageCode: string) => {
    console.log('🌐 Language change requested:', languageCode);
    console.log('🌐 Current language before change:', i18n.language);
    
    try {
      // 모바일 앱과 동일한 forceChangeLanguage 함수 사용
      await forceChangeLanguage(languageCode);
      console.log('🌐 Language changed successfully to:', i18n.language);
      
      // 강제 리렌더링을 위한 상태 업데이트
      window.dispatchEvent(new Event('languageChanged'));
      
      // 페이지 새로고침으로 확실한 변경
      setTimeout(() => {
        window.location.reload();
      }, 300);
      
    } catch (error) {
      console.error('🌐 Language change failed:', error);
      // 에러 발생 시에도 페이지 새로고침
      window.location.reload();
    }
    
    setIsOpen(false);
    console.log('🌐 Language change completed:', languageCode);
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 모바일 스타일 변형 (국기와 약자만)
  if (variant === 'mobile') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white bg-white/10 border border-white/30 rounded-md hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
        >
          <span className="text-sm font-medium text-white">{getCountryAndLanguageCode(currentLanguage).countryCode}</span>
          <span className="text-xs font-bold bg-white/20 text-white px-1 rounded">{getCountryAndLanguageCode(currentLanguage).languageCode}</span>
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="fixed inset-x-4 top-20 bg-white rounded-lg shadow-2xl z-[9999999] border-2 border-gray-200 max-h-60 overflow-y-auto">
            {supportedLanguages.map((langCode) => (
              <button
                key={langCode}
                onClick={() => handleLanguageChange(langCode)}
                className={`w-full text-left px-4 py-3 text-base font-bold border-b border-gray-100 last:border-b-0 ${
                  currentLanguage === langCode 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-1">
                    <span className="text-sm font-medium">{getCountryAndLanguageCode(langCode).countryCode}</span>
                    <span className="text-xs font-bold bg-blue-100 text-blue-800 px-1 rounded">{getCountryAndLanguageCode(langCode).languageCode}</span>
                  </div>
                  <span className="text-xs text-gray-600">{languageNames[langCode as keyof typeof languageNames]?.split(' ')[1] || langCode.toUpperCase()}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // 버튼 스타일 변형
  if (variant === 'button') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <span className="text-sm font-medium">{getCountryAndLanguageCode(currentLanguage).countryCode}</span>
          <span className="text-xs font-bold bg-blue-100 text-blue-800 px-1 rounded">{getCountryAndLanguageCode(currentLanguage).languageCode}</span>
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute left-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded-md shadow-2xl z-[9999999] max-h-60 overflow-y-auto">
            {supportedLanguages.map((langCode) => (
              <button
                key={langCode}
                onClick={() => handleLanguageChange(langCode)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-100 transition-colors border-b border-gray-100 last:border-b-0 ${
                  currentLanguage === langCode ? 'bg-blue-100 text-blue-800 font-bold' : 'text-gray-900 font-semibold'
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-1">
                    <span className="text-sm font-medium">{getCountryAndLanguageCode(langCode).countryCode}</span>
                    <span className="text-xs font-bold bg-blue-100 text-blue-800 px-1 rounded">{getCountryAndLanguageCode(langCode).languageCode}</span>
                  </div>
                  <span className="text-xs text-gray-600">{languageNames[langCode as keyof typeof languageNames]?.split(' ')[1] || langCode.toUpperCase()}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // 기본 드롭다운 스타일
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <span className="text-sm font-medium">{getCountryAndLanguageCode(currentLanguage).countryCode}</span>
          <span className="text-xs font-bold bg-blue-100 text-blue-800 px-1 rounded">{getCountryAndLanguageCode(currentLanguage).languageCode}</span>
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded-lg shadow-2xl z-[9999999] max-h-60 overflow-y-auto">
          <div className="py-1">
            {supportedLanguages.map((langCode) => (
              <button
                key={langCode}
                onClick={() => handleLanguageChange(langCode)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-100 transition-colors border-b border-gray-100 last:border-b-0 ${
                  currentLanguage === langCode ? 'bg-blue-100 text-blue-800 font-bold' : 'text-gray-900'
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-1">
                    <span className="text-sm font-medium">{getCountryAndLanguageCode(langCode).countryCode}</span>
                    <span className="text-xs font-bold bg-blue-100 text-blue-800 px-1 rounded">{getCountryAndLanguageCode(langCode).languageCode}</span>
                  </div>
                  <span className="text-xs text-gray-600">{languageNames[langCode as keyof typeof languageNames]?.split(' ')[1] || langCode.toUpperCase()}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
